import { gsap } from "gsap";
import { useId, useRef } from "react";

import { MOTION_DURATION, MOTION_EASE } from "@/shared/components/motion/motion.tokens";
import { cn } from "@/shared/lib/utils";

import { useIllustrationLoop } from "./useIllustrationLoop";

/** Sample codes the drawing cycles through; illustrative only, never a real code. */
const SAMPLE_CODES = ["482 915", "730 264", "159 803", "624 071"] as const;

/** How long the ring takes to drain. Shorter than the real 30 s so the renewal reads in a glance. */
const DRAIN_SECONDS = 3.2;

const RING_RADIUS = 7.5;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

/**
 * One cycle per code: the countdown ring drains, the current code rolls up and out, the next one rolls in and
 * the ring refills. Built once for every code, so the loop returns to the first code seamlessly.
 */
function buildAuthenticatorLoop(svg: SVGSVGElement): gsap.core.Timeline {
  const codes = Array.from(svg.querySelectorAll<SVGTextElement>("[data-code]"));
  const ring = svg.querySelector<SVGCircleElement>("[data-ring]");
  const timeline = gsap.timeline({ repeat: -1, paused: true });
  if (!ring || codes.length === 0) return timeline;

  gsap.set(codes, { opacity: 0, y: 8 });
  gsap.set(codes[0], { opacity: 1, y: 0 });
  gsap.set(ring, { strokeDasharray: RING_LENGTH, strokeDashoffset: 0 });

  codes.forEach((code, index) => {
    const next = codes[(index + 1) % codes.length];
    timeline
      .to(ring, { strokeDashoffset: RING_LENGTH, duration: DRAIN_SECONDS, ease: MOTION_EASE.linear })
      .to(code, { y: -8, opacity: 0, duration: MOTION_DURATION.quick, ease: MOTION_EASE.enter })
      .fromTo(
        next,
        { y: 8, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: MOTION_DURATION.quick * 1.4,
          ease: MOTION_EASE.enter,
          // Built ahead of time for every code: rendering the from-state now would hide the first code.
          immediateRender: false,
        },
        "<0.08",
      )
      .to(ring, { strokeDashoffset: 0, duration: MOTION_DURATION.quick * 1.6, ease: MOTION_EASE.enter }, "<");
  });
  return timeline;
}

export interface AuthenticatorIllustrationProps {
  className?: string;
}

/**
 * The authenticator app in miniature: a phone and, beside it, the six-digit code it shows, with the countdown
 * ring that drains until a new code replaces it. It makes "a new code on your phone every 30 seconds" visible
 * without a word. Decorative — the card's text already says it. Without motion, the first code and a full
 * ring.
 */
export function AuthenticatorIllustration({ className }: AuthenticatorIllustrationProps) {
  const ref = useRef<SVGSVGElement>(null);
  const clipId = `auth-code-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  useIllustrationLoop(ref, buildAuthenticatorLoop);

  return (
    <svg
      ref={ref}
      viewBox="0 0 148 76"
      className={cn("h-[76px] w-auto max-w-full text-success", className)}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <clipPath id={clipId}>
          <rect x="56" y="22" width="60" height="32" />
        </clipPath>
      </defs>

      {/* The phone */}
      <rect
        x="10"
        y="6"
        width="34"
        height="64"
        rx="7"
        className="fill-card"
        stroke="currentColor"
        strokeOpacity="0.55"
        strokeWidth="1.5"
      />
      <rect x="22" y="10" width="10" height="2" rx="1" fill="currentColor" fillOpacity="0.45" />
      <rect x="15" y="17" width="24" height="10" rx="2.5" fill="currentColor" fillOpacity="0.16" />
      <rect x="15" y="31" width="24" height="5" rx="2" fill="currentColor" fillOpacity="0.1" />
      <rect x="15" y="39" width="17" height="5" rx="2" fill="currentColor" fillOpacity="0.1" />
      <rect x="15" y="47" width="24" height="5" rx="2" fill="currentColor" fillOpacity="0.1" />
      <rect x="23" y="61" width="8" height="2" rx="1" fill="currentColor" fillOpacity="0.35" />

      {/* What the app shows: the code, on a bubble tied to the phone */}
      <path d="M44 38 L52 34 L52 42 Z" className="fill-card" />
      <path
        d="M44 38 L52 34 M44 38 L52 42"
        stroke="currentColor"
        strokeOpacity="0.3"
        strokeWidth="1.2"
        fill="none"
      />
      <rect
        x="52"
        y="18"
        width="88"
        height="40"
        rx="10"
        className="fill-card"
        stroke="currentColor"
        strokeOpacity="0.35"
        strokeWidth="1.2"
      />
      <g clipPath={`url(#${clipId})`}>
        {SAMPLE_CODES.map((code, index) => (
          <text
            key={code}
            data-code=""
            x="84"
            y="43"
            textAnchor="middle"
            className="fill-foreground font-geist-mono"
            fontSize="12.5"
            fontWeight="600"
            letterSpacing="0.6"
            opacity={index === 0 ? 1 : 0}
          >
            {code}
          </text>
        ))}
      </g>

      {/* The countdown to the next code */}
      <circle cx="127" cy="38" r={RING_RADIUS} fill="none" className="stroke-muted" strokeWidth="2.4" />
      <circle
        data-ring=""
        cx="127"
        cy="38"
        r={RING_RADIUS}
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        transform="rotate(-90 127 38)"
      />
    </svg>
  );
}
